// Hit registration check of the perf probe (`?perf` only; docs/NETCODE.md "Lag compensation"):
// every (input tick, target) the local player's tracer ended in a displayed body (fx.ts) against
// the server's `Hit` for that tick and target (messages.ts). A shown hit is confirmed, lost (no
// `Hit` came), or a `Hit` came for a body the tracer did not end in (unshown). For every lost one
// it keeps what the client knew: the render tick the tracer was tested at, the time the server
// rewinds to, extrapolation, the zone, and a replay of the bullet against the target where the
// snapshots put it at the rewound time. Read through `window.__gunrunPerf.hitreg()`.

import type { HitboxRay, ShotTarget } from "../game/shotTrace";
import { TICK_RATE } from "../net/constants";

/** A shown hit counts as lost once the newest snapshot is this many ticks past its tick (0.25 s). */
const RESOLVE_TICKS = Math.round(0.25 * TICK_RATE);
/** The server's lag compensation limits (gr-server `lagcomp.rs`): offset up to RTT + 100 ms, rewind up to 250 ms. */
const REWIND_SLACK_TICKS = 0.1 * TICK_RATE;
const MAX_REWIND_TICKS = 0.25 * TICK_RATE;
const TICK_MS = 1000 / TICK_RATE;
const RANGE = 250;
const LOG_CAP = 4000;

/** A displayed player as the hit check sees it: whether it is alive, and its hitbox target. */
export interface HitregSample {
  alive: boolean;
  target: ShotTarget;
}

export interface ShownHit {
  tick: number;
  target: number;
  /** 0 head, 1 body, 2 limb. */
  zone: number;
  dist: number;
  /** Horizontal speed of the target as displayed (m/s). */
  speed: number;
  /** Remote time the tracer was tested at (the shot's moment; before the fix, the frame's render tick). */
  renderTick: number;
  /** Remote time the server rewinds to, with the view offset limited by the client's RTT. */
  rewindTick: number;
  /** `renderTick` minus the newest snapshot tick (above 0: extrapolated). */
  pastNewest: number;
  /** The view offset was more than the server believes for the RTT. */
  clamped: boolean;
  feet: [number, number, number];
  origin: [number, number, number];
  dir: [number, number, number];
}

export interface LostHit extends ShownHit {
  /** The target was dead in the snapshot before the shot's tick: the server skips it (null: snapshot unknown). */
  dead: boolean | null;
  /** The bullet meets the target where the snapshots put it at `rewindTick` (null: not there). */
  replayHit: boolean | null;
  /** Distance between the target as displayed and at `rewindTick` (m). */
  posErr: number | null;
}

interface FrameContext {
  offsetTicks: number;
  newestTick: number;
  rttMs: number;
}

export class HitregProbe {
  shown = 0;
  confirmed = 0;
  lost = 0;
  unshown = 0;
  /** Sum and count of `renderTick - rewindTick` over the shown hits (ticks). */
  private renderMinusRewindSum = 0;
  readonly lostLog: LostHit[] = [];
  readonly unshownLog: { tick: number; target: number; zone: number; damage: number }[] = [];
  private pending: ShownHit[] = [];
  private frame: FrameContext = { offsetTicks: 0, newestTick: 0, rttMs: 0 };
  private shotTick = 0;
  private shotSubtick = 0;
  private shotRemoteTick = 0;
  private sample: ((tick: number, id: number) => HitregSample | null) | null = null;
  private ray: HitboxRay | null = null;

  /** The snapshot buffer (`sample`: a player at a remote time) and the hit test for the lost-hit replay. */
  attach(sample: (tick: number, id: number) => HitregSample | null, ray: HitboxRay): void {
    this.sample = sample;
    this.ray = ray;
  }

  /** A frame that simulates ticks: the view offset its inputs carry, the newest snapshot tick and the RTT. */
  beginFrame(offsetTicks: number, newestTick: number, rttMs: number): void {
    const f = this.frame;
    f.offsetTicks = offsetTicks;
    f.newestTick = newestTick;
    f.rttMs = rttMs;
  }

  /** The local volley about to be traced: fired in input tick `tick` at `subtick`, tested against the players at `remoteTick`. */
  volley(tick: number, subtick: number, remoteTick: number): void {
    this.shotTick = tick;
    this.shotSubtick = subtick;
    this.shotRemoteTick = remoteTick;
  }

  /** A bullet of the current volley ended in the displayed body of `t`. */
  bullet(t: ShotTarget, dist: number, zone: number, origin: { x: number; y: number; z: number }, dx: number, dy: number, dz: number): void {
    const tick = this.shotTick;
    if (this.pending.some((p) => p.tick === tick && p.target === t.id)) return;
    const f = this.frame;
    const maxOffset = Math.min(f.rttMs / TICK_MS + REWIND_SLACK_TICKS, MAX_REWIND_TICKS);
    const t0 = tick - 1 + this.shotSubtick - Math.min(f.offsetTicks, maxOffset);
    const rewindTick = Math.min(tick, Math.max(tick - MAX_REWIND_TICKS, t0));
    this.shown++;
    this.renderMinusRewindSum += this.shotRemoteTick - rewindTick;
    this.pending.push({
      tick,
      target: t.id,
      zone,
      dist,
      speed: t.speed,
      renderTick: this.shotRemoteTick,
      rewindTick,
      pastNewest: this.shotRemoteTick - f.newestTick,
      clamped: f.offsetTicks > maxOffset,
      feet: [t.pos.x, t.pos.y, t.pos.z],
      origin: [origin.x, origin.y, origin.z],
      dir: [dx, dy, dz],
    });
  }

  /** The server's `Hit` for the shot of input tick `tick` on `target`. */
  hit(tick: number, target: number, zone: number, damage: number): void {
    const i = this.pending.findIndex((p) => p.tick === tick && p.target === target);
    if (i >= 0) {
      this.pending.splice(i, 1);
      this.confirmed++;
      return;
    }
    this.unshown++;
    if (this.unshownLog.length < LOG_CAP) this.unshownLog.push({ tick, target, zone, damage });
  }

  /** A snapshot of tick `newest` arrived: shown hits old enough without a `Hit` are lost. */
  expire(newest: number): void {
    const pending = this.pending;
    let n = 0;
    while (n < pending.length && pending[n].tick + RESOLVE_TICKS <= newest) n++;
    if (n === 0) return;
    for (const h of pending.splice(0, n)) {
      this.lost++;
      if (this.lostLog.length < LOG_CAP) this.lostLog.push(this.explain(h));
    }
  }

  private explain(h: ShownHit): LostHit {
    const atShot = this.sample?.(h.tick - 1, h.target) ?? null;
    const atRewind = this.sample?.(h.rewindTick, h.target) ?? null;
    let replayHit: boolean | null = null;
    let posErr: number | null = null;
    if (atRewind && this.ray) {
      const [ox, oy, oz] = h.origin;
      const [dx, dy, dz] = h.dir;
      replayHit = this.ray(ox, oy, oz, dx, dy, dz, RANGE, atRewind.target).length >= 2;
      const p = atRewind.target.pos;
      posErr = Math.hypot(p.x - h.feet[0], p.y - h.feet[1], p.z - h.feet[2]);
    }
    return { ...h, dead: atShot ? !atShot.alive : null, replayHit, posErr };
  }

  reset(): void {
    this.shown = this.confirmed = this.lost = this.unshown = 0;
    this.renderMinusRewindSum = 0;
    this.lostLog.length = 0;
    this.unshownLog.length = 0;
    this.pending.length = 0;
  }

  dump(): unknown {
    return {
      shown: this.shown,
      confirmed: this.confirmed,
      lost: this.lost,
      unshown: this.unshown,
      pending: this.pending.length,
      renderMinusRewindMean: this.shown > 0 ? this.renderMinusRewindSum / this.shown : 0,
      lostLog: this.lostLog,
      unshownLog: this.unshownLog,
    };
  }
}
